/**
 * Enforce setup on direct dashboard entry as well as the login redirect.
 * An unfinished existing member can carry BOTH real roles and SetupBootstrap.
 * Refresh once to recover a stale post-setup token, then redirect once. If the
 * setup page sends an unfinished user back, show a recovery screen instead of
 * looping or rendering the dashboard. A token without the setup signal clears
 * the per-user recovery flags.
 */
import * as React from "react";
import { useSession } from "@authe/ui-core/session";
import { withSessionHandoff } from "@authe/fe-commons/session";
import { resolveAppUrl } from "@authe/fe-commons/auth";
import { APP_NAME, urlContext } from "@/config";
import { needsSetup } from "./PostSetupTokenGuard.helpers";

/** Registry slug for the onboarding SPA (aliasOf `dashboard`, mounted at /setup). */
const SETUP_APP = "setup";

const REDIRECT_FLAG_PREFIX = "dashboard.postSetupRedirect.";

function readFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    /* storage disabled */
  }
}

function clearFlags(keys: string[]): void {
  try {
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* storage disabled */
  }
}

function setupDestination(subject: string): string {
  const url = new URL(resolveAppUrl({ application: SETUP_APP, path: "/" }, urlContext));
  url.searchParams.set("returnTo", window.location.href);
  return url.origin === window.location.origin ? url.href : withSessionHandoff(url.href, subject);
}

function FinishingSetup(): React.ReactElement {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background text-foreground">
      <div className="size-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
      <p className="text-sm text-muted-foreground">Finishing setup for {APP_NAME}…</p>
    </div>
  );
}

export function PostSetupTokenGuard({ children }: { children: React.ReactNode }): React.ReactElement {
  const { session, manager, refresh } = useSession();
  const setupRequired = needsSetup(session?.claims);
  const [checked, setChecked] = React.useState(false);
  const refreshing = React.useRef<Promise<void> | null>(null);

  React.useEffect(() => {
    if (!session) return;
    const userId = session.user.id ?? "anon";
    const redirectKey = `${REDIRECT_FLAG_PREFIX}${userId}`;

    // Completed setup — clear the one-shot flags so a future
    // hand-off can self-heal again, and let the app render.
    if (!setupRequired) {
      clearFlags([redirectKey]);
      return;
    }

    // Refresh once on THIS mount, including a return from a separate local
    // setup origin. A session-wide "already refreshed" flag stranded that
    // return on the dashboard's old pre-setup cache. Re-project via the session
    // provider, without reloading (which would start another refresh cycle).
    if (!checked) {
      let cancelled = false;
      refreshing.current ??= (async () => {
        if (await manager.refreshAccessToken()) await refresh();
      })();
      void refreshing.current.catch(() => { /* setup's own gate offers retry */ }).finally(() => {
        if (!cancelled) setChecked(true);
      });
      return () => { cancelled = true; };
    }

    // (2) Still setup-required after a hard refresh → setup is genuinely
    // unfinished. Send them ONCE to the onboarding SPA to complete it.
    if (!readFlag(redirectKey)) {
      writeFlag(redirectKey);
      window.location.assign(setupDestination(userId));
      return;
    }

    // (3) No more automatic navigation: the recovery view keeps setup required.
  }, [session, setupRequired, manager, refresh, checked]);

  // While the FIRST refresh/redirect is still pending, don't paint the app with
  // incomplete setup. After one refresh + redirect, offer an explicit recovery.
  const userId = session?.user.id ?? "anon";
  const exhausted = readFlag(`${REDIRECT_FLAG_PREFIX}${userId}`);
  if (session && setupRequired) {
    if (!exhausted) return <FinishingSetup />;
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background text-foreground">
        <h1 className="text-xl font-semibold">Finish setting up your account</h1>
        <p className="text-sm text-muted-foreground">Complete setup to continue to your workspace.</p>
        <a className="underline" href={setupDestination(userId)}>Continue setup</a>
      </div>
    );
  }
  return <>{children}</>;
}
